import React, { useRef, useState } from 'react';
import styled from 'styled-components';

import { IconButton, Menu, MenuItem } from '@workboard/wobo-ui';
import SvgIcon from '@material-ui/core/SvgIcon';
import LinkIcon from '@material-ui/icons/Link';
import BarChart from '@material-ui/icons/BarChart';
import Waves from '@material-ui/icons/Waves';
import PersonOutlined from '@material-ui/icons/PersonOutlined';
import ArchiveOutlined from '@material-ui/icons/ArchiveOutlined';
import RemoveCircleOutline from '@material-ui/icons/RemoveCircleOutline';
import { MoreVert } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import useCopyToClipboard from '../hooks/useCopyToClipboard';
import Separator from '../common/Separator';
import PinFilledIcon from '../../../../vectors/pin-filled.svg';
import PinFilledStarIcon from '../../../../vectors/pin-filled-star.svg';
import RequestToJoin from '../../../../vectors/request-to-join.svg';

const MenuButton = styled(IconButton)`
  height: 32px;
  width: 32px;
  margin: 0 8px;

  span {
    height: 20px;
  }
  svg {
    width: 20px;
    height: 20px;
  }
`;

const MenuItemWrapper = styled(MenuItem)`
  gap: 8px;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  width: 240px;
  height: 40px;
  box-sizing: border-box;
  overflow: hidden;
  min-width: 0;

  svg {
    width: 20px;
    height: 20px;
    flex: 0 0 auto;
  }

  &.disabled {
    opacity: 0.5;
  }

  &.requestCheck svg {
    width: 18px;
    height: 18px;
  }
`;

const Label = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
`;

const StyledMenu = styled(Menu)`
  & .MuiPaper-root,
  & .MuiMenu-paper {
    width: 240px;
    max-width: 240px;
  }
  & .MuiList-root {
    width: 240px;
    max-width: 240px;
  }
`;

const TeamMenu = ({
  isFavorite,
  id,
  userId,
  url,
  role,
  isActive,
  requestEnabled,
  onClickPin,
  onClickArchieve,
  onClickRequest,
  type,
}): JSX.Element => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const anchorRef = useRef<HTMLElement | null>(null);
  const isTeamSettingsRestrictions =
    window.wbConfig.isTeamSettingsRestrictions ?? false;
  const managerCoManagerCheck =
    type !== 2
      ? window.wbConfig.addTeamMemManagerCoManagerFunCheck
      : window.wbConfig.addTeamMemManagerCoManagerDynamicCheck;
  const orgAdminCheck =
    type !== 2
      ? window.wbConfig.addTeamMemOrgAdminFunCheck
      : window.wbConfig.addTeamMemOrgAdminDynamicCheck;
  // CORE-13007: Team Admin was excluded from roleParent when Team Settings restrictions
  // are on, so the "Add Team Member" action was hidden for Team Admins. Check the
  // team_admin permission flag and include it in the roleCheck.
  const teamAdminAddCheck =
    type !== 2
      ? window.wbConfig.addTeamMemTeamAdminFunCheck
      : window.wbConfig.addTeamMemTeamAdminDynamicCheck;
  const isTeamAdminOnTeam = role === 'Team admin';
  const roleParent = isTeamSettingsRestrictions
    ? role === 'Manager' || role === 'Co-manager'
    : role === 'Manager' || role === 'Co-manager' || role === 'Team admin';
  const roleCheck =
    role === 'Manager' || role === 'Co-manager' || role === 'Team admin';
  const roleCheckTeamMember = isTeamSettingsRestrictions
    ? orgAdminCheck ||
      (roleParent && managerCoManagerCheck) ||
      (isTeamAdminOnTeam && teamAdminAddCheck)
    : roleParent;
  const requestCheck = requestEnabled && role === null;
  const readFlag = window.wbConfig.userLicenseType == '0';
  const isUserReadOnly = window.wbConfig.userLicenseOrgFeatureFlag;
  const onMenuClick = (): void => {
    setOpen(!open);
    setAnchorEl(anchorRef.current);
  };
  const [copy] = useCopyToClipboard();
  const closeMenu = (): void => {
    setOpen(false);
    setAnchorEl(null);
  };

  const addTeamOkr = (): void => {
    window.openAddEditObjectivePopup({ tid: id });
    closeMenu();
  };

  const addTeamWorkstream = (): void => {
    $.post(
      '/wb/tag/addWorksream',
      { teamId: id, columnId: 0 },
      (data: never) => {
        window.openCreateWsDialog(data);
      },
    );
    closeMenu();
  };

  const addTeamMember = (): void => {
    const addUrl = `/wb/team/AddTeamMembers?tid=${id}}`;
    $(document).scrollTop(0);
    if (!$(window.team_dialog).hasClass('ui-dialog-content')) {
      window.loadTeamPopupBoxes();
    }
    window.team_dialog.html('');

    $.post(addUrl)
      .done((data: string) => {
        window.team_dialog.html(data);
      })
      .always(() => {
        window.team_dialog.dialogInit();
        window.team_dialog.dialog('option', 'top', 45);
        window.team_dialog.dialog(
          'option',
          'dialogClass',
          'add-team-member-popup',
        );
        window.team_dialog.dialog('option', 'width', 600);
        window.team_dialog.dialog('option', 'position', ['auto', 15]);
        window.team_dialog.dialog('open');
      });
    closeMenu();
  };

  const leaveTeam = (): void => {
    closeMenu();
    window.fnRemoveUserFromTeam(id, userId, 1, true);
  };

  const copyTeam = async (): Promise<void> => {
    const res = await copy(url);

    if (res) {
      toastr.success(t('Link copied to your clipboard.'));
    } else {
      toastr.error(t('Clipboard not supported.'));
    }
  };

  return (
    <>
      <MenuButton
        title={t('More actions')}
        onClick={onMenuClick}
        ref={anchorRef}
      >
        <MoreVert />
      </MenuButton>
      <StyledMenu
        open={open}
        anchorEl={anchorEl}
        onClose={closeMenu}
        keepMounted
      >
        {requestCheck && (
          <MenuItemWrapper
            className="requestCheck"
            onClick={() => onClickRequest(id)}
          >
            <SvgIcon>
              <RequestToJoin />
            </SvgIcon>
            <Label>{t('Request to join')}</Label>
          </MenuItemWrapper>
        )}
        {requestCheck && <Separator />}
        <MenuItemWrapper onClick={onClickPin}>
          <SvgIcon>
            {isFavorite ? <PinFilledStarIcon /> : <PinFilledIcon />}
          </SvgIcon>
          <Label>
            {isFavorite
              ? `${t('Remove from Favorites')}`
              : `${t('Add to Favorites')}`}
          </Label>
        </MenuItemWrapper>
        <MenuItemWrapper onClick={copyTeam}>
          <LinkIcon />
          <Label>{t('Copy link')}</Label>
        </MenuItemWrapper>
        {role !== null && <Separator />}
        {readFlag && isUserReadOnly
          ? roleCheck && (
              <MenuItemWrapper onClick={addTeamOkr} disabled>
                <BarChart />
                <Label>{t('Add team OKR')}</Label>
              </MenuItemWrapper>
            )
          : roleCheck && (
              <MenuItemWrapper onClick={addTeamOkr}>
                <BarChart />
                <Label>{t('Add team OKR')}</Label>
              </MenuItemWrapper>
            )}
        {readFlag && isUserReadOnly
          ? roleCheck && (
              <MenuItemWrapper onClick={addTeamWorkstream} disabled>
                <Waves />
                <Label>{t('Add team Workstream')}</Label>
              </MenuItemWrapper>
            )
          : roleCheck && (
              <MenuItemWrapper onClick={addTeamWorkstream}>
                <Waves />
                <Label>{t('Add team Workstream')}</Label>
              </MenuItemWrapper>
            )}
        {readFlag && isUserReadOnly
          ? roleCheckTeamMember && (
              <MenuItemWrapper onClick={addTeamMember} disabled>
                <PersonOutlined />
                <Label>{t('Add team member')}</Label>
              </MenuItemWrapper>
            )
          : roleCheckTeamMember && (
              <MenuItemWrapper onClick={addTeamMember}>
                <PersonOutlined />
                <Label>{t('Add team member')}</Label>
              </MenuItemWrapper>
            )}
        {roleCheckTeamMember && <Separator />}
        {readFlag && isUserReadOnly
          ? roleCheck && (
              <MenuItemWrapper onClick={onClickArchieve} disabled>
                <ArchiveOutlined />
                <Label>
                  {isActive ? `${t('Archive')}` : `${t('Unarchive')}`}
                </Label>
              </MenuItemWrapper>
            )
          : roleCheck && (
              <MenuItemWrapper onClick={onClickArchieve}>
                <ArchiveOutlined />
                <Label>
                  {isActive ? `${t('Archive')}` : `${t('Unarchive')}`}
                </Label>
              </MenuItemWrapper>
            )}
        {readFlag && isUserReadOnly
          ? role !== 'Manager' &&
            role !== null && (
              <MenuItemWrapper onClick={leaveTeam} disabled>
                <RemoveCircleOutline />
                <Label>{t('Leave team')}</Label>
              </MenuItemWrapper>
            )
          : role !== 'Manager' &&
            role !== null && (
              <MenuItemWrapper onClick={leaveTeam}>
                <RemoveCircleOutline />
                <Label>{t('Leave team')}</Label>
              </MenuItemWrapper>
            )}
      </StyledMenu>
    </>
  );
};

export default TeamMenu;
